import { FC, FormEvent } from 'react';

import { Link } from '@inertiajs/react';
import { useForm, usePage } from '@inertiajs/react';
import { clsx } from 'clsx';

import { GoogleIcon } from '../../components/shared/GoogleIcon';
import { OauthProfileCompletionDialog } from '../../components/shared/OauthProfileCompletionDialog';
import { Button } from '../../components/ui/Button';
import { CardContent, CardFooter } from '../../components/ui/Card';
import { Checkbox } from '../../components/ui/Checkbox';
import { Input } from '../../components/ui/Input';
import { Separator } from '../../components/ui/Separator';
import { Routes } from '../../models/routes';

type FormValues = {
    email: string;
    password: string;
    remember_me: boolean;
};

type PageProps = {
    oauthProfileCompletionRequired?: boolean;
};

export const SignInForm: FC = () => {
    const { oauthProfileCompletionRequired } = usePage<PageProps>().props;
    const { data, setData, transform, post, processing, errors } =
        useForm<FormValues>({
            email: '',
            password: '',
            remember_me: false,
        });

    function submit(e: FormEvent) {
        e.preventDefault();
        transform((formData) => ({ user: formData }));
        post(Routes.SignIn);
    }

    return (
        <>
            <OauthProfileCompletionDialog open={Boolean(oauthProfileCompletionRequired)} />

            <form onSubmit={submit}>
                <CardContent className="grid gap-4">
                    <div className="grid gap-2">
                        <label
                            className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            htmlFor="email"
                        >
                            Електронна пошта
                        </label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="m@example.com"
                            autoCapitalize="none"
                            autoComplete="email"
                            autoCorrect="off"
                            className={clsx({
                                'border-red-600': errors.email,
                            })}
                            value={data.email}
                            onChange={(e) => setData('email', e.target.value)}
                        />
                    </div>
                    <div className="grid gap-2">
                        <div className="flex items-center justify-between">
                            <label
                                className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                htmlFor="password"
                            >
                                Пароль
                            </label>
                            <Link
                                href={Routes.ForgotPassword}
                                className="text-sm font-medium text-primary hover:underline"
                            >
                                Забули пароль?
                            </Link>
                        </div>
                        <Input
                            id="password"
                            type="password"
                            autoComplete="current-password"
                            className={clsx({
                                'border-red-600': errors.password,
                            })}
                            onChange={(e) =>
                                setData('password', e.target.value)
                            }
                        />
                        {errors.password && (
                            <p className="text-sm text-red-600">
                                {errors.password}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="remember"
                            checked={data.remember_me}
                            onCheckedChange={(checked) =>
                                setData('remember_me', !!checked)
                            }
                        />
                        <label
                            htmlFor="remember"
                            className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            Запам&#39;ятати мене
                        </label>
                    </div>
                </CardContent>
                <CardFooter className="flex flex-col pt-2">
                    <Button
                        className="h-10 w-full text-sm font-semibold"
                        type="submit"
                        disabled={processing}
                    >
                        Увійти
                    </Button>

                    <div className="my-4 flex w-full items-center gap-3">
                        <Separator className="flex-1" />
                        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                            або
                        </span>
                        <Separator className="flex-1" />
                    </div>

                    <Button
                        asChild
                        variant="outline"
                        className="h-10 w-full gap-2 text-sm font-semibold"
                    >
                        <a href={Routes.GoogleAuth}>
                            <GoogleIcon className="h-4 w-4" />
                            Увійти через Google
                        </a>
                    </Button>

                    <p className="mt-4 text-center text-sm text-muted-foreground">
                        Немає облікового запису?{' '}
                        <Link
                            href={Routes.SignUp}
                            className="font-medium text-primary hover:underline"
                        >
                            Зареєструватися
                        </Link>
                    </p>
                </CardFooter>
            </form>
        </>
    );
};
